import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, Lock, Eye, EyeOff, CheckCircle2, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

// AUTH-03 — Password reset (multi-step: request → sent → reset)
export default function PasswordResetPage() {
  const [step, setStep] = useState("request"); // request | sent | reset | done
  const [email, setEmail] = useState("");
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const navigate = useNavigate();

  const strength = pwdStrength(pwd);
  const matches = pwd === pwd2 && pwd.length >= 12;

  if (step === "request") {
    return (
      <div className="space-y-6">
        <Link to="/login" className="inline-flex items-center gap-1.5 text-caption text-ink-subtle hover:text-ink">
          <ArrowLeft className="size-3.5" /> Back to sign-in
        </Link>
        <div>
          <div className="eyebrow">Reset password</div>
          <h1 className="font-brand text-h1 text-ink">Forgot your password?</h1>
          <p className="mt-2 text-caption text-ink-subtle leading-relaxed">
            Enter your email and we&rsquo;ll send a reset link. Links are valid for 60 minutes and can only be used once.
          </p>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); setStep("sent"); }} className="space-y-4" data-testid="reset-request-form">
          <div>
            <Label className="text-caption font-semibold text-ink mb-1.5 block">Email</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                   placeholder="you@example.com" required autoFocus data-testid="reset-email" />
          </div>
          <Button type="submit" className="w-full h-11 bg-brand-700 hover:bg-brand-800 text-white" data-testid="reset-send-btn">
            Send reset link
          </Button>
        </form>
        <div className="border-t border-line pt-4 text-center text-caption text-ink-subtle">
          New here? <Link to="/apply" className="text-brand-700 font-semibold hover:text-brand-800">Apply to join</Link>
        </div>
      </div>
    );
  }

  if (step === "sent") {
    return (
      <div className="space-y-6 text-center">
        <div className="mx-auto size-12 rounded-full bg-brand-50 text-brand-700 grid place-items-center">
          <Mail className="size-6" />
        </div>
        <div>
          <h1 className="font-brand text-h1 text-ink">Check your email</h1>
          <p className="mt-2 text-body-sm text-ink-muted leading-relaxed">
            If <span className="font-semibold text-ink">{email}</span> matches an account, a reset link is on its way.
            Check your spam folder if you don&rsquo;t see it within five minutes.
          </p>
        </div>
        <Button onClick={() => setStep("reset")} className="w-full bg-brand-700 hover:bg-brand-800 text-white">
          I&rsquo;ve clicked the link · Set new password
        </Button>
        <div className="text-caption text-ink-subtle">
          Didn&rsquo;t get the email?{" "}
          <button onClick={() => { toast.success("Reset email re-sent"); }} className="text-brand-700 hover:text-brand-800 font-semibold">Resend</button>
        </div>
        <div className="border-t border-line pt-4 text-caption text-ink-subtle">
          <Link to="/login" className="hover:text-ink">Back to sign-in</Link>
        </div>
      </div>
    );
  }

  if (step === "reset") {
    return (
      <div className="space-y-6">
        <div>
          <div className="eyebrow">Set new password</div>
          <h1 className="font-brand text-h1 text-ink">Choose a strong password</h1>
          <p className="mt-2 text-caption text-ink-subtle leading-relaxed">
            Minimum 12 characters. We recommend a passphrase like four random words.
          </p>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); setStep("done"); setTimeout(() => navigate("/login"), 1500); }}
              className="space-y-4" data-testid="reset-form">
          <div>
            <Label className="text-caption font-semibold text-ink mb-1.5 block">New password</Label>
            <div className="relative">
              <Input type={showPwd ? "text" : "password"} value={pwd} onChange={(e) => setPwd(e.target.value)}
                     required autoFocus data-testid="reset-new-pwd" />
              <button type="button" onClick={() => setShowPwd((s) => !s)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 size-7 grid place-items-center text-ink-hint hover:text-ink">
                {showPwd ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
              </button>
            </div>
            <Strength s={strength} />
          </div>
          <div>
            <Label className="text-caption font-semibold text-ink mb-1.5 block">Confirm password</Label>
            <Input type={showPwd ? "text" : "password"} value={pwd2} onChange={(e) => setPwd2(e.target.value)}
                   required data-testid="reset-confirm-pwd" />
            {pwd2.length > 0 && !matches && <p className="mt-1 text-caption text-red-600">Passwords don&rsquo;t match.</p>}
          </div>
          <ul className="text-caption space-y-1 text-ink-muted">
            <Check label="At least 12 characters" met={pwd.length >= 12} />
            <Check label="A mix of letters and numbers (or a passphrase)" met={/[A-Za-z]/.test(pwd) && (/\d/.test(pwd) || pwd.split(/\s+/).length >= 4)} />
            <Check label="Not used on other sites" met={pwd.length >= 12} />
          </ul>
          <Button type="submit" disabled={!matches || strength < 3}
                  className="w-full h-11 bg-brand-700 hover:bg-brand-800 text-white" data-testid="reset-save-btn">
            Save new password
          </Button>
        </form>
      </div>
    );
  }

  // step === "done"
  return (
    <div className="space-y-6 text-center">
      <div className="mx-auto size-12 rounded-full bg-emerald-50 text-emerald-700 grid place-items-center">
        <CheckCircle2 className="size-6" />
      </div>
      <h1 className="font-brand text-h1 text-ink">Password updated</h1>
      <p className="text-body-sm text-ink-muted">Redirecting you back to sign-in…</p>
    </div>
  );
}

function pwdStrength(pwd) {
  if (!pwd) return 0;
  let s = 0;
  if (pwd.length >= 8) s++;
  if (pwd.length >= 12) s++;
  if (/[A-Z]/.test(pwd) && /[a-z]/.test(pwd)) s++;
  if (/\d/.test(pwd) || /\W/.test(pwd)) s++;
  return Math.min(4, s);
}

function Strength({ s }) {
  const labels = ["", "Weak", "Fair", "Good", "Strong"];
  const colors = ["bg-line", "bg-red-500", "bg-amber-500", "bg-sky-500", "bg-emerald-500"];
  return (
    <div className="mt-2 flex items-center gap-2">
      <div className="flex gap-1 flex-1">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className={`h-1 flex-1 rounded-full transition-colors ${i <= s ? colors[s] : "bg-line"}`} />
        ))}
      </div>
      <span className="text-caption text-ink-subtle w-12 text-right">{labels[s]}</span>
    </div>
  );
}

function Check({ label, met }) {
  return (
    <li className="flex items-center gap-2">
      <span className={`size-3.5 rounded-full grid place-items-center ${met ? "bg-emerald-500 text-white" : "bg-line text-ink-hint"}`}>
        {met && <CheckIcon />}
      </span>
      <span className={met ? "text-ink" : "text-ink-muted"}>{label}</span>
    </li>
  );
}
function CheckIcon() {
  return <svg viewBox="0 0 24 24" width="9" height="9" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>;
}
